import { Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import { humanize } from "../../lib/format";

const PILL = {
  confirmed: "bg-green-100 text-green-800", completed: "bg-green-100 text-green-800", approved: "bg-green-100 text-green-800",
  published: "bg-green-100 text-green-800", logged: "bg-sky-100 text-sky-800",
  pending: "bg-amber-100 text-amber-900", pending_payment: "bg-amber-100 text-amber-900", pending_approval: "bg-amber-100 text-amber-900",
  under_review: "bg-amber-100 text-amber-900", new: "bg-sky-100 text-sky-800", draft: "bg-slate-200 text-slate-800",
  failed: "bg-red-100 text-red-800", cancelled: "bg-red-100 text-red-800", declined: "bg-red-100 text-red-800",
  refunded: "bg-purple-100 text-purple-800",
};

export const StatusPill = ({ value, testid }) => (
  <span className={`inline-block whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold ${PILL[value] || "bg-slate-100 text-slate-700"}`} data-testid={testid}>
    {humanize(value)}
  </span>
);

export const TestBadge = ({ show = true }) => show ? <span className="rounded bg-gold px-1.5 py-0.5 text-[10px] font-bold text-navy">TEST</span> : null;

export const PageHeader = ({ title, subtitle, children }) => (
  <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
    <div><h1 className="text-3xl text-navy">{title}</h1>{subtitle && <p className="mt-1 text-[#4A5568]">{subtitle}</p>}</div>
    <div className="flex flex-wrap items-center gap-3">{children}</div>
  </div>
);

export const InfoTip = ({ text, label }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <button type="button" className="text-slate-500 hover:text-navy" aria-label={`Definition: ${label}`} data-testid={`metric-info-${label.toLowerCase().replace(/\W+/g, "-")}`}>
        <Info size={15} aria-hidden="true" />
      </button>
    </TooltipTrigger>
    <TooltipContent className="max-w-xs">{text}</TooltipContent>
  </Tooltip>
);

export const NextStage = ({ title, items }) => (
  <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-5" data-testid="next-stage-note">
    <p className="font-semibold text-navy">{title}: coming in next stage</p>
    {items && <ul className="mt-2 list-disc pl-5 font-mono text-xs text-[#4A5568]">{items.map((i) => <li key={i}>{i}</li>)}</ul>}
  </div>
);

export const DataTable = ({ columns, rows, onRowClick, empty = "No records.", testid }) => (
  <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white" data-testid={testid}>
    <table className="w-full text-left text-sm">
      <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-600">
        <tr>{columns.map((c) => <th key={c.key} scope="col" className="px-4 py-3 font-semibold">{c.label}</th>)}</tr>
      </thead>
      <tbody>
        {rows.length === 0 && <tr><td colSpan={columns.length} className="px-4 py-8 text-center text-slate-500">{empty}</td></tr>}
        {rows.map((r, i) => (
          <tr key={r.id || i} className={`border-t border-slate-100 ${onRowClick ? "cursor-pointer hover:bg-paleblue" : ""}`}
            onClick={onRowClick ? () => onRowClick(r) : undefined} data-testid={`${testid}-row-${i}`}>
            {columns.map((c) => <td key={c.key} className="px-4 py-3 align-top">{c.render ? c.render(r) : String(r[c.key] ?? "")}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);
